import axios, { AxiosError } from "axios";

let interceptorId: number | null = null;

/**
 * Normalises JSON error bodies that arrive as a `Blob`.
 *
 * Every `responseType: "blob"` request that goes through the GLOBAL axios
 * default (32 call sites — CSV/PDF downloads across admin, investor and
 * common) gets its ERROR body as a Blob too, not just its success body. That
 * breaks two things downstream:
 *
 *  1. `isSessionInvalidation401` reads `error.response.data.code` — on a Blob
 *     that is `undefined`, so a session-kill 401 on a download is classified
 *     as a plain 401. The generic interceptor then redirects silently and the
 *     session interceptor never runs its cleanup + "Session Ended" overlay.
 *  2. `getApiResponseErrorMsg` reads `data.message` and falls back to
 *     `statusText` — which is ALWAYS "" over HTTP/2, so every download error
 *     toast degrades to the generic "Something went wrong" string.
 *
 * Re-reading the Blob as JSON here fixes both at once, because this runs
 * before the 401 interceptors AND before each service's own catch block.
 *
 * MUST be registered FIRST at app entry (src/index.tsx), ahead of
 * setupUnauthorizedInterceptor and the session interceptor — Axios runs
 * response interceptors in registration order. An async rejection handler is
 * fine: Axios chains them as promise handlers, so the later ones await this.
 *
 * We parse-and-catch rather than sniffing `data.type` for "json": gateways
 * routinely drop the content-type on error responses, and a genuine binary
 * error body simply fails to parse and is left untouched.
 *
 * `packages/info` is deliberately excluded, which is why the count above is 32
 * and not 33. Its one blob download (services/getAuditDocument.ts) goes
 * through a private `axios.create()` instance (info/src/utils/reactQuery.tsx),
 * so interceptors on the global default never see it — registering this at
 * info's entry point would be a no-op. Info also has its own self-contained
 * 401 handling and no session overlay, so neither failure mode above applies
 * there. Fixing it would mean registering on that instance instead.
 */
export const setupBlobErrorNormalizer = (): void => {
  // Idempotent no-op rather than eject+re-append: re-registering would move
  // this handler BEHIND the 401 interceptors and defeat its whole purpose.
  // Tests that need a clean slate call removeBlobErrorNormalizer() first.
  if (interceptorId !== null) {
    return;
  }

  interceptorId = axios.interceptors.response.use(undefined, async (error: AxiosError) => {
    const data = error.response?.data;

    if (data instanceof Blob) {
      try {
        error.response!.data = JSON.parse(await data.text());
      } catch {
        // Not JSON (a real CSV/PDF error payload, or an empty body) — leave
        // the Blob in place so callers that want the raw bytes still get them.
      }
    }

    return Promise.reject(error);
  });
};

/**
 * Removes the blob-error normaliser. Tests that register it should call this
 * in afterEach so it doesn't leak into later files sharing the worker's
 * module registry under vitest `isolate: false`.
 */
export const removeBlobErrorNormalizer = (): void => {
  if (interceptorId !== null) {
    axios.interceptors.response.eject(interceptorId);
    interceptorId = null;
  }
};
